/*
 * PORTED FROM cres-t5 `src/components/Catalog/RepeatingGroup.tsx`. Kept textually as close to it as
 * possible -- only the axios helper and the endpoint differ -- because two form renderers that
 * drift would draw the same record two ways. If you change one, change the other.
 *
 * t1 draws a record of an OBJECT; t5 draws one of its own Records screen. Both ask the SAME service
 * for the definition, so what a field means has one answer.
 */
import React, {useEffect, useState} from "react";
import {Button, IconButton, Tooltip, Typography} from "@mui/material";
import {AddOutlined, DeleteOutlined} from "@mui/icons-material";
import Axios from "../../util/Axios";
import {Grey6, Grey17} from "../../theme/styles";
import {Control, Field} from "./FormRenderer";
import {Problem, problemsForCell} from "../../util/formProblems";
import {contextForRow, contextKey, dependentTriggers, distinctContexts} from "../../util/rowContext";
import css_buttons from "../Common/css/CCButton.module.scss";

// A repeating group, drawn as a grid: one row per element, one column per field of the shape it
// holds.
//
// THE COLUMNS COME FROM THE SHAPE, fetched once. A composite field's type IS the shape of one
// element -- that is what the model change made true -- so the child definition is read the same way
// the parent's was, and the cells reuse the parent's own Control. A second control table for grid
// cells is how a Select in a form and a Select in a grid come to offer different things.
//
// VALIDITY IS STILL THE SERVER'S ANSWER. Problems arrive carrying the path the server assigned --
// `contacts[1].name` -- and are matched to the cell by that path rather than recomputed here. So a
// required field inside a group is marked by the same evaluation that refuses the write, and a rule
// nobody drew on screen still cannot be saved past.
//
// OPTIONS ARE RESOLVED PER ROW, because a dependent picker narrows against the record it sits in and
// inside a grid that record is the ROW -- the server validates a group by recursing with the element
// as the whole context, so a child field's `parentFrom` names a sibling in the same row. Resolving
// once per column would offer every row the unnarrowed list, which is the choice the dependency
// exists to prevent.
//
// KEYED ON THE CONTEXT, NOT THE ROW, which is what stops that being a request per row per keystroke.
// Options are a function of the trigger answers and nothing else, so ten rows that all chose the
// same Supplier Type share one request, and a row whose triggers have not changed makes none.

type Props = {
    /** The field holding the group, so its type says what one element looks like. */
    field: Field;
    /** The elements, as stored. */
    value: any[];
    /** Problems whose path points inside this group. */
    problems: Problem[];
    onChange: (next: any[]) => void;
    readOnly?: boolean;
};

export default function RepeatingGroup({field, value, problems, onChange, readOnly}: Props) {
    const [columns, setColumns] = useState<Field[]>([]);
    // Per-context column definitions: {contextKey: {fieldCode: Field}}. Only dependent columns are
    // stored, because nothing else changes with the row.
    const [narrowed, setNarrowed] = useState<Record<string, Record<string, Field>>>({});
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const elements: any[] = Array.isArray(value) ? value : [];
    const triggers = dependentTriggers(columns);

    useEffect(() => {
        Axios({method: "POST", url: "t1/v1/object_form_definition",
                   data: {CompositeRevisionUSID: field.DataTypeRevisionUSID}})
            .then((response: any) => {
                // A retired field keeps its column only while some row still answers it, for the
                // same reason a retired field keeps its place on a form.
                const all: Field[] = response?.data?.Fields ?? [];
                setColumns(all.filter(f => !f.Retired ||
                    elements.some(e => (e ?? {})[f.Code] != null)));
                setLoading(false);
            })
            .catch((e: any) => {
                setError(e?.response?.data?.Code ?? "This group's shape could not be loaded.");
                setLoading(false);
            });
        // Elements are deliberately not a dependency: the shape does not change as rows are added,
        // and re-reading on every keystroke would be a request per character typed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.DataTypeRevisionUSID]);

    // One request per DISTINCT context still needed. Debounced so a trigger that is a text box does
    // not fire per character; a trigger that is a picker settles immediately after the choice.
    const wanted = triggers.length
        ? distinctContexts(elements, triggers).filter(c => !narrowed[c.key])
        : [];
    const wantedKeys = JSON.stringify(wanted.map(c => c.key));

    useEffect(() => {
        const pending: {key: string; context: Record<string, any>}[] = JSON.parse(wantedKeys).length
            ? wanted : [];
        if (!pending.length) return;

        const timer = setTimeout(() => {
            pending.forEach(({key, context}) => {
                Axios({method: "POST", url: "t1/v1/object_form_definition",
                           data: {CompositeRevisionUSID: field.DataTypeRevisionUSID,
                                  Context: context}})
                    .then((response: any) => {
                        const byCode: Record<string, Field> = {};
                        (response?.data?.Fields ?? []).forEach((f: Field) => { byCode[f.Code] = f; });
                        setNarrowed(previous => ({...previous, [key]: byCode}));
                    })
                    .catch(() => { /* The column's unnarrowed definition remains; the save still
                                      refuses a value the row does not admit. */ });
            });
        }, 250);
        return () => clearTimeout(timer);
        // Keyed on which contexts are outstanding, not on the rows: a change that leaves the
        // triggers alone leaves this alone too.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wantedKeys, field.DataTypeRevisionUSID]);

    // The column as THIS row sees it. A dependent column falls back to its unnarrowed definition
    // only while the answer is in flight, and that definition reports RequiresContext, so the cell
    // says "choose X first" rather than offering everything for a moment.
    const columnFor = (column: Field, element: any): Field => {
        if (!(column.RequiresContext ?? []).length) return column;
        const key = contextKey(contextForRow(element, triggers));
        return narrowed[key]?.[column.Code] ?? column;
    };

    const setCell = (index: number, code: string, next: any) =>
        onChange(elements.map((element, i) =>
            i === index ? {...(element ?? {}), [code]: next} : element));

    const addRow = () => onChange([...elements, {}]);

    const removeRow = (index: number) => onChange(elements.filter((_, i) => i !== index));

    if (loading) return <Typography style={{fontSize: 13, color: Grey6}}>Loading…</Typography>;
    if (error) return <Typography style={{fontSize: 13, color: "#C9820A"}}>{error}</Typography>;

    return (
        <div>
            {elements.length === 0 ? (
                <Typography style={{fontSize: 13, color: Grey6, marginBottom: 8}}>
                    No {field.TypeName} rows yet.
                </Typography>
            ) : (
                <div style={{overflowX: "auto", border: "1px solid #D8DDE4", borderRadius: 5}}>
                    <table style={{borderCollapse: "collapse", width: "100%"}}>
                        <thead>
                            <tr>
                                {columns.map(column => (
                                    <th key={column.Code}
                                        style={{textAlign: "left", padding: "8px 10px",
                                                borderBottom: "1px solid #D8DDE4",
                                                fontSize: 12, fontWeight: 600, color: Grey17,
                                                whiteSpace: "nowrap"}}>
                                        {column.Label}
                                        {column.Required ? (
                                            // Accurate for a column that is ALWAYS required. One
                                            // required only under a condition is not marked here,
                                            // because the columns are read once for the whole grid
                                            // and a condition is answered per row.
                                            <span style={{color: "#C0392B", marginLeft: 3}}>*</span>
                                        ) : null}
                                        {column.Retired ? (
                                            <span style={{fontWeight: 400, color: Grey6}}> · retired</span>
                                        ) : null}
                                    </th>
                                ))}
                                {readOnly ? null : <th style={{width: 44, borderBottom: "1px solid #D8DDE4"}}/>}
                            </tr>
                        </thead>
                        <tbody>
                            {elements.map((element, index) => (
                                <tr key={index}>
                                    {columns.map(column => (
                                        <td key={column.Code}
                                            style={{padding: "6px 10px", verticalAlign: "top",
                                                    borderTop: index === 0 ? "none" : "1px solid #E6EAF0",
                                                    minWidth: 160}}>
                                            {(() => {
                                                const resolved = columnFor(column, element);
                                                return (resolved.RequiresContext ?? []).length ? (
                                                    // The dependency, said in the cell. An empty
                                                    // picker with no explanation is what this
                                                    // whole channel exists to prevent.
                                                    <Typography style={{fontSize: 12, color: Grey6}}>
                                                        Choose {resolved.RequiresContext
                                                            .map(c => columns.find(f => f.Code === c)?.Label ?? c)
                                                            .join(" and ")} first.
                                                    </Typography>
                                                ) : (
                                                    <Control field={resolved}
                                                             value={(element ?? {})[column.Code]}
                                                             readOnly={!!readOnly}
                                                             onChange={(next: any) =>
                                                                 setCell(index, column.Code, next)}/>
                                                );
                                            })()}
                                            {problemsForCell(problems, index, column.Code).map((problem, i) => (
                                                <Typography key={i}
                                                            style={{fontSize: 12, color: "#C0392B",
                                                                    marginTop: 4}}>
                                                    {problem.Detail}
                                                </Typography>
                                            ))}
                                        </td>
                                    ))}
                                    {readOnly ? null : (
                                        <td style={{padding: "6px 4px", verticalAlign: "top",
                                                    borderTop: index === 0 ? "none" : "1px solid #E6EAF0"}}>
                                            <Tooltip title="Remove this row">
                                                <IconButton onClick={() => removeRow(index)}>
                                                    <DeleteOutlined sx={{fontSize: 20, color: Grey6}}/>
                                                </IconButton>
                                            </Tooltip>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {readOnly ? null : (
                <Button className={css_buttons.blueOutlinedButton}
                        style={{height: 30, marginTop: 8}}
                        startIcon={<AddOutlined/>}
                        onClick={addRow}>
                    Add {field.TypeName}
                </Button>
            )}
        </div>
    );
}
